import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import api from 'services';
import { ApiError } from 'services/api.errors';
import { PortfolioTransactionsType } from 'services/requests';
import { CommonCustomError } from 'types/errors';
import { useQueryDateRange } from 'hooks/routes';
import { formatInputDateForBE } from 'utils';
import { QueryKeys } from 'constants/queryKeys';
import { useGetAllAssets } from './useGetAllAssets';

export const useGetEtzProfileTransactions = () => {
  const { isSuccess, data: allAssets } = useGetAllAssets();
  const [{ from: fromQuery, to: toQuery }] = useQueryDateRange();

  const startDateString = fromQuery ? formatInputDateForBE(fromQuery) : '';
  const endDateString = toQuery ? formatInputDateForBE(toQuery) : '';
  const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const queryFn = () => async (): Promise<PortfolioTransactionsType> => {
    if (!allAssets) {
      throw new CommonCustomError();
    }

    const { data } = await api.getEtzProfileTransactions(
      startDateString,
      endDateString,
      timeZoneId,
    );

    const items = data.items.map((dataItem) => {
      const assetInfo = allAssets[dataItem.asset.id];
      if (!assetInfo) {
        throw new CommonCustomError();
      }
      const baseAssetInfo = allAssets[dataItem.baseAsset.id];
      if (!baseAssetInfo) {
        throw new CommonCustomError();
      }

      return {
        ...dataItem,
        asset: { ...assetInfo, amount: dataItem.asset.amount },
        baseAsset: { ...baseAssetInfo, amount: dataItem.baseAsset.amount },
      };
    });

    return {
      items,
    };
  };

  const queryKey = [
    QueryKeys.etzProfileTransactions,
    startDateString,
    endDateString,
    timeZoneId,
  ];

  return useQuery<PortfolioTransactionsType, AxiosError<ApiError>>(queryKey, queryFn(), {
    enabled: isSuccess && !!fromQuery && !!toQuery,
    retry: false,
    refetchOnWindowFocus(query) {
      return query.state.status !== 'error';
    },
  });
};
